<script setup lang="ts">
import { computed, onMounted, watch, ref } from "vue";
import { FormInput, Checkbox, Submit } from "@/ts/components/form";
import { useForm } from "vee-validate";
import * as Yup from "yup";
import { useErrors } from "@/ts/utils/api-functions";
import { trans, wTrans, isLoaded } from "laravel-vue-i18n";
import { useReCaptcha } from "vue-recaptcha-v3";
import { MembershipData } from "@/ts/components/members";
import axios from "axios";
import { serialize } from "object-to-formdata";

interface Props {
    data: MembershipData["personal_info"];
    showBack?: boolean;
}

const apiErrors = useErrors();
const reCaptcha = useReCaptcha();
const success = ref(false);
const loading = ref(false);
const mounted = ref(false);
const component = ref<HTMLDivElement>();
const props = defineProps<Props>();

const emit = defineEmits(["submit", "back", "mounted"]);

const schema = computed(() =>
    Yup.object({
        first_name: Yup.string().required().label(wTrans("membership.personal_info.first_name").value),
        last_name: Yup.string().required().label(wTrans("membership.personal_info.last_name").value),
        email: Yup.string().email().required().label(wTrans("membership.personal_info.email").value),
        phone: Yup.string()
            .matches(/(((\+|00)3[12][ ]?(?:\(0\)[ ]?)?)|0){1}(4(60|[789]\d)\/?(\s?\d{2}\.?){2}(\s?\d{2})|(\d\/?\s?\d{3}|\d{2}\/?\s?\d{2})(\.?\s?\d{2}){2})/, "Telefoonnummer is ongeldig")
            .required()
            .label(wTrans("membership.personal_info.phone").value),
        street: Yup.string().required().label(wTrans("membership.personal_info.street").value),
        number: Yup.string().required().label(wTrans("membership.personal_info.number").value),
        addition: Yup.string().optional().label(wTrans("membership.personal_info.addition").value),
        postal_code: Yup.string()
            .matches(/^[1-9]{1}[0-9]{3}$/, "Postcode is ongeldig")
            .required()
            .label(wTrans("membership.personal_info.postal_code").value),
        city: Yup.string().required().label(wTrans("membership.personal_info.city").value),
        birth_date: Yup.string()
            .matches(/^(0?[1-9]|[12][0-9]|3[01])[\/\-](0?[1-9]|1[012])[\/\-]\d{4}$/, "Geboortedatum is ongeldig")
            .required()
            .label(wTrans("membership.personal_info.birth_date").value),
        privacy: Yup.bool().oneOf([true]),
    }),
);

const { handleSubmit, errors, meta, isSubmitting } = useForm({
    initialValues: props.data,
    validationSchema: schema,
});

const onSubmit = handleSubmit(async (values, { resetForm }) => {
    loading.value = true;
    try {
        await reCaptcha!.recaptchaLoaded();
        const token = await reCaptcha!.executeRecaptcha("login");
        const formData = serialize(values);
        formData.append("g-recaptcha-response", token);
        formData.append("_wpcf7_unit_tag", "9326487");
        await axios.post("/api/membership", formData);
        success.value = true;
        emit("submit", values);
    } catch (error) {
        apiErrors.parse(error);
    } finally {
        loading.value = false;
    }
});

onMounted(() => {
    let translationsChecker = setInterval(() => {
        if (isLoaded()) {
            mounted.value = true;
            emitComponentHeight();
            clearInterval(translationsChecker);
        }
    }, 50);

    window.addEventListener("resize", () => {
        emitComponentHeight();
    });

    window.addEventListener("orientationchange", () => {
        emitComponentHeight();
    });

    // scroll to top
    window.scrollTo(0, 0);
});

function emitComponentHeight() {
    if (component.value) {
        emit("mounted", component.value!.offsetHeight);
    }
}

watch(
    errors,
    async () => {
        setTimeout(() => {
            emitComponentHeight();
        }, 50);
    },
    { deep: true },
);

watch(
    meta,
    async () => {
        setTimeout(() => {
            emitComponentHeight();
        }, 50);
    },
    { deep: true },
);
</script>

<template>
    <div ref="component" class="absolute top-0 left-0 w-full px-4 pt-10 pb-4 md:p-10 lg:p-[3.75rem]">
        <template v-if="showBack">
            <button class="group mb-4 inline-flex items-center text-14 uppercase !leading-normal" @click="emit('back')">
                <span class="mr-2 rotate-180 transform transition-transform group-hover:-translate-x-1">
                    <svg-vue icon="arrow" class="h-4 w-auto"></svg-vue>
                </span>
                Terug
            </button>
            <h3 class="mb-6 text-center font-heading text-32 font-[690] uppercase [font-stretch:_190%] md:text-left lg:text-32">{{ trans("membership.personal_info_renew_title") }}</h3>
        </template>
        <template v-else>
            <h2 class="mb-10 text-center font-heading text-40 font-[690] uppercase [font-stretch:_190%] md:mb-4 lg:text-60">{{ trans("membership.title") }}</h2>
            <h3 class="mb-6 text-center font-heading text-32 font-[690] uppercase [font-stretch:_190%] md:text-left lg:text-32">{{ trans("membership.personal_info_title") }}</h3>
        </template>
        <form @submit="onSubmit" class="grid grid-cols-4 gap-4">
            <form-input class="col-span-4 md:col-span-2" name="first_name" :auto-focus="true" :placeholder="trans('membership.personal_info.first_name')" :label="trans('membership.personal_info.first_name')"></form-input>
            <form-input class="col-span-4 md:col-span-2" name="last_name" :placeholder="trans('membership.personal_info.last_name')" :label="trans('membership.personal_info.last_name')"></form-input>
            <form-input class="col-span-4 md:col-span-2" name="phone" :placeholder="trans('membership.personal_info.phone')" :label="trans('membership.personal_info.phone')"></form-input>
            <form-input class="col-span-4 md:col-span-2" name="email" :placeholder="trans('membership.personal_info.email')" :label="trans('membership.personal_info.email')"></form-input>
            <form-input class="col-span-4 md:col-span-2" name="street" :placeholder="trans('membership.personal_info.street')" :label="trans('membership.personal_info.street')"></form-input>
            <form-input class="col-span-2 md:col-span-1" name="number" :placeholder="trans('membership.personal_info.number')" :label="trans('membership.personal_info.number')"></form-input>
            <form-input class="col-span-2 md:col-span-1" name="addition" :placeholder="trans('membership.personal_info.addition')" :label="trans('membership.personal_info.addition')"></form-input>
            <form-input class="col-span-4 md:col-span-2" name="postal_code" :placeholder="trans('membership.personal_info.postal_code')" :label="trans('membership.personal_info.postal_code')"></form-input>
            <form-input class="col-span-4 md:col-span-2" name="city" :placeholder="trans('membership.personal_info.city')" :label="trans('membership.personal_info.city')"></form-input>
            <form-input class="col-span-4 md:col-span-2" name="birth_date" mask="##/##/####" placeholder="dd/mm/yyyy" :label="trans('membership.personal_info.birth_date')"></form-input>
            <checkbox name="privacy" class="col-span-4 md:mt-6">
                <span v-html="trans('membership.privacy_policy')"></span>
            </checkbox>
            <div class="col-span-4 flex md:mt-6 md:justify-end">
                <submit class="btn btn-black w-full md:w-auto" :disabled="success" :loading="isSubmitting || loading">
                    <!--{{ trans("membership.next") }}-->
                    {{ trans("membership.become_member") }}
                    <svg-vue icon="arrow" class="ml-[.625rem] h-6 w-auto"></svg-vue>
                </submit>

                <component class="mt-10" :is="apiErrors.render()" :data="apiErrors.get()"></component>
            </div>
        </form>
    </div>
</template>
